import searchService, { MAX_SIZE } from './search';

import { PREFIX_SOURCE } from '../../Map/services/cluster';
import { LAYER_TYPES_ORDER } from '../../Map/services/mapUtils';

export const INITIAL_FILTERS = new Map();

export const isCluster = (source, layerId) => !!source.match(new RegExp(`^${layerId}-${PREFIX_SOURCE}-[0-9]+`));

/**
 * Returns a flattened map of layers state from a layers tree
 *
 * @param {object} layersTree The layers tree config object
 * @param {string|string[]} layers Active layer(s) from hash
 * @param {string} table Active table from hash (layer id)
 * @return {Map} A reduced layer tree state
 */
export const getLayersTreeIds = (group, ids = []) => {
  group.forEach(layer => {
    if (layer.group) {
      getLayersTreeIds(layer.layers, ids);
      return;
    }
    const { layers: [layerId] = [] } = layer;
    if (layerId !== undefined) ids.push(layerId);
  });
  return ids;
};

export const initLayersStateAction = (layersTree, { layers: hashLayers, table } = {}) => {
  const layersTreeState = new Map();

  const requestedIds = (Array.isArray(hashLayers) ? hashLayers : [hashLayers])
    .filter(id => id !== undefined && id !== null && id !== '');
  const knownIds = getLayersTreeIds(layersTree);
  const hashMatchesTree = requestedIds.some(id => knownIds.includes(id));

  function reduceLayers (group, map) {
    return group.reduce((layersStateMap, layer) => {
      const { initialState: layerInitialState = {}, layers: [layerId] = [] } = layer;
      if (layer.group) {
        return reduceLayers(layer.layers, layersStateMap);
      }
      const initialState = { ...layerInitialState };
      initialState.opacity = initialState.opacity === undefined
        ? 1
        : initialState.opacity;

      if (hashMatchesTree) {
        initialState.active = requestedIds.includes(layerId);
      }

      if (table && table === layerId) {
        initialState.table = true;
      }

      layersStateMap.set(layer, {
        active: false,
        opacity: 1,
        ...initialState,
      });

      return layersStateMap;
    }, map);
  }

  return reduceLayers(layersTree, layersTreeState);
};

export function setGroupLayerStateAction (layer, layerState, prevLayersTreeState) {
  const { layers } = layer;
  let layersTreeState = prevLayersTreeState;
  const defaultIndex = Math.max(0, layers.findIndex(({ default: isDefault }) => isDefault));

  layers.forEach((sublayer, index) => {
    const newLayerState = { ...layerState };
    if (newLayerState.active && index !== defaultIndex) {
      newLayerState.active = false;
    }
    // One must be before the other
    // eslint-disable-next-line no-use-before-define
    layersTreeState = setLayerStateAction(sublayer, newLayerState, layersTreeState);
  });
  return layersTreeState;
}

/**
 * Compute new `newLayersTreeState` from `prevLayersTreeState`
 * with updated `layerState` for `layer`. If we want to `reset` state.
 * Do not update state.
 */
export const setLayerStateAction = (layer, layerState, prevLayersTreeState, reset) => {
  if (layer.group) return setGroupLayerStateAction(layer, layerState, prevLayersTreeState);

  const newLayersTreeState = new Map(prevLayersTreeState);
  // Clone layer current state
  const prevLayerState = newLayersTreeState.get(layer);
  if (!prevLayerState) return prevLayersTreeState;

  const newLayerState = { ...layerState };

  if (newLayerState.active === false) {
    newLayerState.table = false;
  }

  if (newLayerState.table) {
    Array.from(newLayersTreeState).forEach(([node, layState]) => {
      newLayersTreeState.set(node, { ...layState, table: false });
    });
  }
  newLayersTreeState.set(layer, {
    ...reset ? {} : prevLayerState,
    ...newLayerState,
  });
  return newLayersTreeState;
};

export function layersTreeStatesHaveChanged (layersTreeState, prevLayersTreeState, fields = ['active']) {
  return !fields.reduce((all, field) =>
    all && Array.from(layersTreeState).reduce((suball, [layer, state = {}]) =>
      suball && state[field] === (prevLayersTreeState.get(layer) || {})[field],
    all),
  true);
}

export const filterLayersStatesFromLayersState = (
  layersTreeState = [],
  check = ({ active }) => !!active,
) => Array
  .from(layersTreeState)
  .reduce((allLayers, [layer, state]) =>
    ((check(state)) ? [...allLayers, [layer, state]] : allLayers), []);

export const filterLayersFromLayersState = (layersTreeState, check) =>
  filterLayersStatesFromLayersState(layersTreeState, check)
    .filter(([{ filters: { layer } = {} }]) => layer)
    .map(([{ filters: { layer } }]) => layer);

export const hasTable = layersTreeState =>
  filterLayersFromLayersState(layersTreeState, ({ table }) => table).length > 0;

export const hasWidget = layersTreeState =>
  filterLayersStatesFromLayersState(layersTreeState, ({ widgets = [] }) => widgets.length > 0)
    .length > 0;

const filterFromFeatureIds = ids => {
  if (!Array.isArray(ids)) {
    return null;
  }

  if (!ids.length) {
    return false;
  }

  return ['match', ['to-string', ['get', '_id']], ids.map(id => `${id}`), true, false];
};

export const filterFeatures = (
  map,
  features/* = { id: { features: [id1, id2, …], layers [id1, id2,...] }} */,
  layersTreeState,
) => {
  Array.from(layersTreeState).forEach(([{
    layers,
    id,
  }, {
    active,
  }]) => {
    if (!active || !layers) return;

    // Keep only features ids for current layer id
    const { features: ids } = features[id] || {};

    layers.forEach(mapLayerId => {
      const paintLayer = map.getLayer(mapLayerId);
      if (!paintLayer) return;

      if (!INITIAL_FILTERS.has(mapLayerId)) {
        INITIAL_FILTERS.set(mapLayerId, map.getFilter(mapLayerId));
      }

      if (!ids) {
        // no feature defined for this layer so we reset the filter
        map.setFilter(mapLayerId, INITIAL_FILTERS.get(mapLayerId));
        return;
      }

      // Generate mapbox filter from id list
      const filter = filterFromFeatureIds(ids);

      map.setFilter(mapLayerId, filter);
    });
  });
};

export const resetFilters = (map, layersTreeState) => {
  Array.from(layersTreeState).forEach(([{
    layers = [], filters,
  }]) => {
    if (!filters) return;

    layers.forEach(layerId => {
      const paintLayer = map.getLayer(layerId);

      if (!map.getLayer(layerId)) return;

      if (isCluster(paintLayer.source, layerId)) {
        // Force to upgrade cluster data
        map.fire('refreshCluster');
        return;
      }
      if (!INITIAL_FILTERS.has(layerId)) return;
      map.setFilter(layerId, INITIAL_FILTERS.get(layerId));
    });
  });
};

const flattenLayersTreeNodeIds = layersTree => layersTree.reduce(
  (all, node) => [
    ...all,
    ...(node.group ? flattenLayersTreeNodeIds(node.layers) : [node.id]),
  ],
  [],
);

export const sortCustomLayers = (customLayers, layersTree) => {
  const nodeIds = flattenLayersTreeNodeIds(layersTree);
  const nodeIdIndex = new Map([...nodeIds].reverse().map((id, i) => [id, i]));

  return [...customLayers].sort((a, b) => {
    const typeA = LAYER_TYPES_ORDER.indexOf(a.type);
    const typeB = LAYER_TYPES_ORDER.indexOf(b.type);
    if (typeA !== typeB) return typeA - typeB;
    const posA = nodeIdIndex.has(a.layerId) ? nodeIdIndex.get(a.layerId) : Infinity;
    const posB = nodeIdIndex.has(b.layerId) ? nodeIdIndex.get(b.layerId) : Infinity;
    return posA - posB;
  });
};

/**
 * Fetch property values list
 * @param {String} layer Layer id to fetch
 * @param {Object} property Property to fetch
 * @return Mixed[]
 */
export const AUTOCOMPLETE_SIZE = 500;

const LUCENE_SPECIAL_CHARS = /[.?+*|{}[\]()"\\#@&<>~]/;

/**
 * Build a case insensitive lucene pattern for the query. Terms aggregations
 * filter on the raw keyword, which lucene matches case sensitively and anchors
 * on the whole value.
 */
export const buildValuesPatterns = query => {
  const escaped = Array.from(query)
    .map(char => {
      if (LUCENE_SPECIAL_CHARS.test(char)) return `\\${char}`;
      const lower = char.toLowerCase();
      const upper = char.toUpperCase();
      return lower === upper ? char : `[${lower}${upper}]`;
    })
    .join('');

  return { startsWith: `${escaped}.*`, contains: `.*${escaped}.*` };
};

const termsOn = (field, name, include) => ({
  type: 'terms',
  field,
  name,
  options: {
    size: AUTOCOMPLETE_SIZE,
    include,
    order: { _key: 'asc' },
  },
});

export const fetchPropertyValues = async (layer, { property }, baseQuery, query = '') => {
  const field = `${property}.keyword`;
  const { startsWith, contains } = query ? buildValuesPatterns(query) : {};

  const results = await searchService.search({
    index: layer,
    baseQuery,
    aggregations: query
      ? [termsOn(field, 'startsWith', startsWith), termsOn(field, 'values', contains)]
      : [{ type: 'terms', field, name: 'values', options: { size: MAX_SIZE } }],
    size: 0,
  });

  const {
    aggregations: {
      values: { buckets },
      startsWith: { buckets: startsWithBuckets = [] } = {},
    },
  } = results;

  // Values starting with the query first, they are the ones being looked for
  return [...new Set([...startsWithBuckets, ...buckets].map(({ key }) => key))];
};

/**
 * Fetch property's min and max values
 * @param {String} layer Layer id to fetch
 * @param {Object} property Property to fetch
 * @return { min: Number, max: Number }
 */
export const fetchPropertyRange = async (layer, { property }, baseQuery) => {
  const results = await searchService.search({
    index: layer,
    baseQuery,
    aggregations: [{
      type: 'max',
      field: `${property}`,
      name: 'max',
    }, {
      type: 'min',
      field: `${property}`,
      name: 'min',
    }],
    size: 0,
  });
  const { aggregations: { min: { value: min } = {}, max: { value: max } = {} } } = results;

  return { min: min && Math.round(min), max: max && Math.round(max) };
};

/**
 * Transform a layersTree into a flat array of layers
 */
const flattenLayersTreeGroup = tree => {
  const layers = [];

  tree.forEach(treeNode => {
    if (treeNode.label) {
      layers.push(treeNode);
    }

    if (treeNode.layers) {
      layers.push(...flattenLayersTreeGroup(treeNode.layers));
    }
  });

  return layers;
};

/**
 * Story type view needs a specfic format.
 */
export const layersTreeToStory = layersTree => {
  const arrayOfLayers = flattenLayersTreeGroup(layersTree);

  const story = arrayOfLayers.reduce(({ beforeEach: [beforeEachConfig], slides }, layer) => {
    const { label: title, content = '', layers = [], legends, ...layerAttrs } = layer;
    const mapboxLayers = layers.filter(l => typeof l === 'string');
    return {
      beforeEach: [{
        ...beforeEachConfig,
        layers: [...beforeEachConfig.layers, ...mapboxLayers],
      }],
      slides: [
        ...slides, {
          title,
          content,
          legends,
          layouts: [{ layers: mapboxLayers, active: true }],
          layerNode: layer,
          ...layerAttrs,
        },
      ],
    };
  }, {
    beforeEach: [{ layers: [], active: false }],
    slides: [],
  });

  return story;
};

export default {
  initLayersStateAction,
  setLayerStateAction,
  filterLayersFromLayersState,
  hasTable,
  hasWidget,
  filterFeatures,
  resetFilters,
  sortCustomLayers,
  fetchPropertyValues,
  fetchPropertyRange,
  layersTreeToStory,
};
